import { useMutation } from '@apollo/client/react';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Theme,
  Typography
} from '@mui/material';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { v4 as uuid } from 'uuid';

import { AppContext } from '../../../App';
import { InputField, Popup } from '../../../components';
import { ComponentWeightBuffer, KDSPermission } from '../../../libs';
import { LIST_KITCHENS_QUERY, UPDATE_KITCHEN_BUFFERS_MUTATION } from '../../../libs/graphQL';

interface ComponentWeightBufferEditorProps {
  componentWeightBufferEditorModalRef: React.RefObject<{ open: () => void; close: () => void }>;
  theme: Theme;
}

const DEFAULT_COMPONENT_WEIGHT_BUFFER: ComponentWeightBuffer = {
  id: uuid(),
  startRange: 1,
  endRange: Number.MAX_SAFE_INTEGER,
  percentage: 100
};

const ComponentWeightBufferEditor: React.FC<ComponentWeightBufferEditorProps> = ({
  componentWeightBufferEditorModalRef,
  theme
}) => {
  const appContext = useContext(AppContext);
  const kdsKitchen = appContext.kdsKitchen;
  const kitchen = kdsKitchen?.id ?? '';
  const canEditBuffer = appContext.user.permissions?.includes(KDSPermission.EDIT_BUFFER) ?? false;

  const [componentWeightBufferRows, setComponentWeightBufferRows] = useState<ComponentWeightBuffer[]>([]);
  const previousComponentWeightBuffersRef = useRef<ComponentWeightBuffer[]>([]);
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [updateKitchenBuffers, { loading }] = useMutation(UPDATE_KITCHEN_BUFFERS_MUTATION, {
    refetchQueries: [{ query: LIST_KITCHENS_QUERY, variables: { kitchen } }],
    onCompleted: () => {
      toast.success('Buffers updated successfully!');
      componentWeightBufferEditorModalRef.current?.close();
    },
    onError: (error) => {
      console.error(error);
      toast.error('Error while updating buffers');
    }
  });

  useEffect(() => {
    const componentWeightBuffers = kdsKitchen?.componentWeightBuffers ?? [DEFAULT_COMPONENT_WEIGHT_BUFFER];

    previousComponentWeightBuffersRef.current = componentWeightBuffers;

    setComponentWeightBufferRows(componentWeightBuffers);
  }, [kdsKitchen?.componentWeightBuffers]);

  const validateWeightBufferRanges = useCallback((componentWeightBufferRows: ComponentWeightBuffer[]) => {
    if (
      [...componentWeightBufferRows]
        .sort((a, b) => a.startRange - b.startRange)
        .some((r, i, arr) => i && arr[i - 1].endRange > r.startRange)
    ) {
      return 'No Range should overlap';
    }

    if (componentWeightBufferRows.some((row) => row.endRange !== Number.MAX_SAFE_INTEGER && row.startRange >= row.endRange)) {
      return 'All start ranges must be less than their end ranges';
    }

    return '';
  }, []);

  const handleInputChange = useCallback((id: string, field: keyof ComponentWeightBuffer, value: number) => {
    setHasChanges(true);
    setComponentWeightBufferRows((prevRows: ComponentWeightBuffer[]) => {
      return prevRows.map((row: ComponentWeightBuffer) => {
        if (row.id !== id) {
          return row;
        }

        const updatedValue =
          field === 'endRange' && (value === null || isNaN(value) || value === 0) ? Number.MAX_SAFE_INTEGER : value;

        return {
          ...row,
          [field]: updatedValue
        };
      });
    });
  }, []);

  const handleAddRow = useCallback(() => {
    setHasChanges(true);
    setComponentWeightBufferRows((prevRows) => {
      const lastRow = prevRows[prevRows.length - 1];
      const newStartRange =
        lastRow.endRange === Number.MAX_SAFE_INTEGER
          ? lastRow.startRange + 1
          : lastRow.endRange
            ? lastRow.endRange + 1
            : lastRow.startRange + 1;
      return [
        ...prevRows.slice(0, -1),
        { ...lastRow, endRange: newStartRange - 1 },
        {
          startRange: newStartRange,
          endRange: Number.MAX_SAFE_INTEGER,
          percentage: lastRow.percentage,
          id: uuid()
        }
      ];
    });
  }, []);

  const handleRemoveRow = useCallback((id: string) => {
    setHasChanges(true);
    setComponentWeightBufferRows((prevRows: ComponentWeightBuffer[]) => {
      if (prevRows.length === 1) {
        toast.error('At least one buffer range is required');
        return prevRows;
      }
      return prevRows.filter((row) => row.id !== id);
    });
  }, []);

  const handleSave = useCallback(() => {
    const validationError = validateWeightBufferRanges(componentWeightBufferRows);

    if (validationError) {
      toast.error(validationError);
      return;
    }

    updateKitchenBuffers({
      variables: {
        kitchen,
        componentWeightBuffers: componentWeightBufferRows.map((row) => ({
          id: row.id,
          startRange: row.startRange,
          endRange: row.endRange,
          percentage: row.percentage
        }))
      }
    }).catch(() => undefined);
  }, [componentWeightBufferRows, kitchen, updateKitchenBuffers]);

  return (
    <Popup
      ref={componentWeightBufferEditorModalRef}
      onClose={componentWeightBufferEditorModalRef.current?.close}
      title="Buffers"
      style={{
        fullWidth: false,
        maxWidth: 'sm'
      }}
      showCloseButton={true}
    >
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '70vh',
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            overflowY: 'auto',
            overflowX: 'hidden',
            flex: 1,
            pr: 1
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, fontSize: '16px' }}>Total Cooked Weight (g) Range</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '16px' }}>Buffers</TableCell>
                <TableCell sx={{ width: 80 }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {componentWeightBufferRows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{
                        alignItems: 'center'
                      }}
                    >
                      <TextField
                        type="number"
                        size="small"
                        value={row.startRange}
                        onChange={(e) => handleInputChange(row.id, 'startRange', parseInt(e.target.value))}
                        sx={{ width: 120 }}
                        disabled={!canEditBuffer}
                        error={row.endRange !== Number.MAX_SAFE_INTEGER && row.startRange >= row.endRange}
                        helperText={
                          row.endRange !== Number.MAX_SAFE_INTEGER && row.startRange >= row.endRange
                            ? 'Start range must be less than end range'
                            : ''
                        }
                        slotProps={{
                          htmlInput: { min: 1, step: 1 }
                        }}
                      />
                      <Typography> - </Typography>
                      <TextField
                        type="number"
                        size="small"
                        value={row.endRange === Number.MAX_SAFE_INTEGER ? '' : row.endRange}
                        onChange={(e) => handleInputChange(row.id, 'endRange', parseInt(e.target.value))}
                        placeholder="∞"
                        sx={{ width: 120 }}
                        disabled={!canEditBuffer}
                        error={row.endRange !== Number.MAX_SAFE_INTEGER && row.startRange >= row.endRange}
                        helperText={
                          row.endRange !== Number.MAX_SAFE_INTEGER && row.startRange >= row.endRange
                            ? 'End range must be greater than start range'
                            : ''
                        }
                        slotProps={{
                          htmlInput: { min: row.startRange + 1, step: 1 }
                        }}
                      />
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <InputField
                      isOutlined
                      type="number"
                      size="small"
                      debounce
                      value={row.percentage}
                      onChange={(value) => handleInputChange(row.id, 'percentage', parseFloat(value as unknown as string))}
                      sx={{ width: 100 }}
                      inputProps={{ min: 0.1, max: 100, step: 0.1 }}
                      endAdornment={<Typography sx={{ color: theme.palette.warning.main }}>%</Typography>}
                      disabled={!canEditBuffer}
                    />
                  </TableCell>
                  <TableCell>
                    {componentWeightBufferRows.length > 1 && (
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveRow(row.id)}
                        sx={{ color: theme.palette.error.main }}
                        disabled={!canEditBuffer}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
        {canEditBuffer && (
          <Box
            sx={{
              mt: 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexShrink: 0,
              pt: 2,
              borderTop: `1px solid ${theme.palette.divider}`
            }}
          >
            <Button
              variant="outlined"
              onClick={handleAddRow}
              sx={{
                textTransform: 'none',
                borderRadius: '8px'
              }}
            >
              Add New Range
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={!hasChanges || loading || !kitchen}
              sx={{
                textTransform: 'none',
                borderRadius: '8px',
                minWidth: 100
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Save'}
            </Button>
          </Box>
        )}
      </Box>
    </Popup>
  );
};

export default ComponentWeightBufferEditor;
